import { BaseRepository } from '../../../infra/base.repository'
import { ConfigService } from '../../../infra/config'
import type { FormData } from './form-data'

export class FormDatasRepository extends BaseRepository<FormData> {
    constructor(
        private config: ConfigService = new ConfigService(),
        token?: string,
        cache?: any,
    ) {
        super(config.toolsApiUrl + '/' + 'form-data', token, cache)
    }

    // Formato antigo, mantido só por retrocompatibilidade com links já enviados a clientes (sem token,
    // apenas formDataId+companyId crus na URL). Links novos devem usar publicGetOneByLink.
    async publicGetOne(companyId: string, formDataId: string) {
        const { json, statusCode, message } = await this.request.get(
            this.config.toolsApiUrl + '/form-data/public/' + formDataId + '?c=' + companyId,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    // Formato novo — id é o formDataLinkId, token é o segredo aleatório do link (mesmo padrão do
    // Forms V2). A mesma rota do formato antigo atende os dois; a presença de "t" na URL decide
    // qual caminho o backend usa. Query key é "t", não "token": "token" já é o parâmetro usado em toda
    // a API para o JWT da sessão logada, e colidia com este quando o link era aberto autenticado.
    async publicGetOneByLink(formDataLinkId: string, token: string) {
        const { json, statusCode, message } = await this.request.get(
            this.config.toolsApiUrl + '/form-data/public/' + formDataLinkId + '?t=' + token,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async getSignedDocumentUrl(formDataId: string): Promise<{ url: string }> {
        const { json, statusCode, message } = await this.request.get(
            this.config.toolsApiUrl + '/form-data/' + formDataId + '/signed-document?token=' + this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    async findLastEmissionsFromPerson(personId: string, formDataIds: string[]) {
        const { json, statusCode, message } = await this.request.get(
            this.config.toolsApiUrl +
                '/form-data/last-emissions?personId=' +
                personId +
                '&ids=' +
                formDataIds +
                '&token=' +
                this.resolvedToken,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    // Formato antigo, mantido por retrocompatibilidade — ver publicGetOne acima.
    async publicUpdate(companyId: string, formDataId: string, entity: Partial<FormData>) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.toolsApiUrl + '/form-data/public/' + formDataId + '?c=' + companyId,
            entity,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }

    // Formato novo — ver publicGetOneByLink acima.
    async publicUpdateByLink(formDataLinkId: string, token: string, entity: Partial<FormData>) {
        const { json, statusCode, message } = await this.request.patch(
            this.config.toolsApiUrl + '/form-data/public/' + formDataLinkId + '?t=' + token,
            entity,
        )
        if (String(statusCode).substring(0, 1) === '2') return json
        throw message
    }
}
